import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Grid3X3,
  Settings,
  Save,
  RefreshCw,
  Package,
  AlertTriangle,
  Plus,
  Minus,
  Rows,
  Columns
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SlotCard = ({ compartment, products, onUpdate, allCompartments }) => {
  const product = products.find(p => p.id === compartment.product_id);
  const stockPercent = compartment.max_capacity > 0 
    ? (compartment.current_stock / compartment.max_capacity) * 100 
    : 0;
  
  let statusClass = "slot-empty";
  if (product) {
    if (stockPercent >= 70) statusClass = "slot-full";
    else if (stockPercent >= 30) statusClass = "slot-ok";
    else statusClass = "slot-low";
  }

  // Calculate MHD status
  let mhdStatus = null;
  let mhdDays = null;
  if (compartment.expiry_date && compartment.current_stock > 0) {
    const today = new Date();
    const expiry = new Date(compartment.expiry_date);
    mhdDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (mhdDays < 0) mhdStatus = "expired";
    else if (mhdDays <= 14) mhdStatus = "critical";
    else if (mhdDays <= 30) mhdStatus = "warning";
  }

  const displayName = compartment.custom_name || compartment.slot_id;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`p-3 rounded-md border-2 text-left transition-all ${statusClass} hover:shadow-md relative`}
          data-testid={`slot-${compartment.slot_id}`}
        >
          {/* MHD Warning Badge */}
          {mhdStatus && (
            <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${
              mhdStatus === "expired" ? "bg-red-500 text-white" :
              mhdStatus === "critical" ? "bg-amber-500 text-white" :
              "bg-yellow-400 text-yellow-900"
            }`}>
              {mhdStatus === "expired" ? "!" : mhdDays}
            </div>
          )}
          <div className="font-mono text-sm font-bold text-slate-600 mb-1">
            {displayName}
          </div>
          {product ? (
            <>
              <div className="text-sm font-medium text-slate-900 truncate">
                {product.name}
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-500">
                  {compartment.current_stock}/{compartment.max_capacity}
                </span>
                {stockPercent < 30 && (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                )}
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full mt-1 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    stockPercent >= 70 ? 'bg-emerald-500' : 
                    stockPercent >= 30 ? 'bg-amber-500' : 'bg-rose-500'
                  }`}
                  style={{ width: `${stockPercent}%` }}
                />
              </div>
              {/* MHD text below stock bar */}
              {compartment.expiry_date && (
                <div className={`text-[10px] mt-1 ${
                  mhdStatus === "expired" ? "text-red-600 font-bold" :
                  mhdStatus === "critical" ? "text-amber-600" :
                  mhdStatus === "warning" ? "text-yellow-700" :
                  "text-slate-400"
                }`}>
                  MHD: {new Date(compartment.expiry_date).toLocaleDateString("de-DE")}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-slate-400 mt-1">Leer</div>
          )}
        </motion.button>
      </DialogTrigger>
      <SlotDialog 
        compartment={compartment} 
        products={products} 
        currentProduct={product}
        onUpdate={onUpdate}
        allCompartments={allCompartments}
      />
    </Dialog>
  );
};

const SlotDialog = ({ compartment, products, currentProduct, onUpdate, allCompartments }) => {
  const [productId, setProductId] = useState(compartment.product_id || "none");
  const [maxCapacity, setMaxCapacity] = useState(compartment.max_capacity);
  const [currentStock, setCurrentStock] = useState(compartment.current_stock);
  const [slotId, setSlotId] = useState(compartment.slot_id);
  const [customName, setCustomName] = useState(compartment.custom_name || "");
  const [expiryDate, setExpiryDate] = useState(compartment.expiry_date || "");
  const [mergeTarget, setMergeTarget] = useState("none");
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/compartments/${compartment.id}`, {
        product_id: productId === "none" ? null : productId,
        max_capacity: maxCapacity,
        current_stock: Math.min(currentStock, maxCapacity),
        slot_id: slotId,
        custom_name: customName || null,
        expiry_date: expiryDate || null
      });
      toast.success("Fach aktualisiert");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    if (mergeTarget === "none") return;
    
    setMerging(true);
    try {
      await axios.post(`${API}/compartments/merge`, {
        source_compartment_id: compartment.id,
        target_compartment_id: mergeTarget
      });
      toast.success("Fächer zusammengelegt");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zusammenlegen");
    } finally {
      setMerging(false);
    }
  };

  // Other compartments for merge selection
  const otherCompartments = allCompartments?.filter(c => c.id !== compartment.id) || [];

  return (
    <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading text-base">
          Fach {compartment.custom_name || compartment.slot_id}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        {/* Rename Section */}
        <div className="space-y-2">
          <Label className="text-sm">Fach-ID</Label>
          <Input
            value={slotId}
            onChange={(e) => setSlotId(e.target.value.toUpperCase())}
            placeholder="z.B. A1"
            data-testid="slot-id-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Anzeigename (optional)</Label>
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="z.B. Großes Fach"
            data-testid="slot-custom-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Produkt zuweisen</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="slot-product-select">
              <SelectValue placeholder="Produkt wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Produkt (leer)</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} - {p.price.toFixed(2)}€
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Füllstand</Label>
          <Input
            type="number"
            min="0"
            max={maxCapacity}
            value={currentStock}
            onChange={(e) => setCurrentStock(Math.min(parseInt(e.target.value) || 0, maxCapacity))}
            data-testid="slot-current-stock-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm">Max. Kapazität</Label>
          <Input
            type="number"
            min="1"
            max="100"
            value={maxCapacity}
            onChange={(e) => setMaxCapacity(parseInt(e.target.value) || 10)}
            data-testid="slot-capacity-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm">MHD</Label>
          <Input
            type="date"
            value={expiryDate ? expiryDate.split('T')[0] : ""}
            onChange={(e) => setExpiryDate(e.target.value)}
            data-testid="slot-expiry-input"
          />
        </div>

        <div className="bg-slate-50 rounded-md p-3">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs text-slate-500">Füllstand</div>
              <div className="text-xl font-heading font-bold text-slate-900">
                {currentStock} / {maxCapacity}
              </div>
            </div>
            <div className="text-right">
              <div className={`text-xl font-heading font-bold ${
                (currentStock / maxCapacity) * 100 >= 70 ? 'text-emerald-600' :
                (currentStock / maxCapacity) * 100 >= 30 ? 'text-amber-600' : 'text-rose-600'
              }`}>
                {maxCapacity > 0 ? Math.round((currentStock / maxCapacity) * 100) : 0}%
              </div>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
          data-testid="slot-save-btn"
        >
          {saving ? "Speichern..." : "Speichern"}
        </Button>

        {/* Merge Section */}
        {otherCompartments.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <Label className="text-amber-700 text-sm">Zusammenlegen</Label>
            <p className="text-xs text-slate-500 mb-2">
              Mit anderem Fach kombinieren
            </p>
            <div className="flex gap-2">
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="merge-target-select" className="flex-1">
                  <SelectValue placeholder="Ziel-Fach" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Auswählen --</SelectItem>
                  {otherCompartments.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.custom_name || c.slot_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={handleMerge} 
                disabled={merging || mergeTarget === "none"}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="merge-btn"
              >
                {merging ? "..." : "Zusammenlegen"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  );
};

const VendingPage = () => {
  const [config, setConfig] = useState({ rows: 4, columns: 6, name: "" });
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [newRows, setNewRows] = useState(4);
  const [newCols, setNewCols] = useState(6);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, compRes, prodRes] = await Promise.all([
        axios.get(`${API}/vending/config`),
        axios.get(`${API}/compartments`),
        axios.get(`${API}/products`)
      ]);
      setConfig(configRes.data);
      setCompartments(compRes.data);
      setProducts(prodRes.data);
      setNewRows(configRes.data.rows);
      setNewCols(configRes.data.columns);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCompartments = async () => {
    try {
      // Update config
      await axios.put(`${API}/vending/config`, {
        rows: newRows,
        columns: newCols
      });
      // Generate compartments
      await axios.post(`${API}/compartments/generate?rows=${newRows}&columns=${newCols}`);
      toast.success("Fächer neu generiert");
      setConfigOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Generieren");
    }
  };

  // Sort compartments by row and column
  const sortedCompartments = [...compartments].sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    return a.column - b.column;
  });

  // Group by rows
  const rows = [];
  for (let r = 0; r < config.rows; r++) {
    rows.push(sortedCompartments.filter(c => c.row === r));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vending-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Automat
          </h1>
          <p className="text-slate-500 mt-1">
            {config.name || "Snackautomat"} · {config.rows} Reihen × {config.columns} Spalten
          </p>
        </div>
        <Dialog open={configOpen} onOpenChange={setConfigOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="btn-hover" data-testid="config-btn">
              <Settings className="w-4 h-4 mr-2" />
              Konfiguration
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Automat konfigurieren</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reihen (A-Z)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="26"
                    value={newRows}
                    onChange={(e) => setNewRows(parseInt(e.target.value) || 1)}
                    data-testid="config-rows-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Spalten (1-n)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={newCols}
                    onChange={(e) => setNewCols(parseInt(e.target.value) || 1)}
                    data-testid="config-cols-input"
                  />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Achtung: Das Neugenerieren der Fächer setzt alle Zuordnungen zurück!
              </div>
              <Button 
                onClick={handleGenerateCompartments}
                className="w-full bg-rose-600 hover:bg-rose-700"
                data-testid="config-generate-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Fächer neu generieren
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Row/Column Controls */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Zeilen:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/compartments/delete-row`);
                    toast.success("Zeile entfernt");
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Fehler");
                  }
                }}
                disabled={config.rows <= 1}
                data-testid="remove-row-btn"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="font-mono font-bold w-8 text-center">{config.rows}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/compartments/add-row`);
                    toast.success("Zeile hinzugefügt");
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Fehler");
                  }
                }}
                data-testid="add-row-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Spalten:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/compartments/delete-column`);
                    toast.success("Spalte entfernt");
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Fehler");
                  }
                }}
                disabled={config.columns <= 1}
                data-testid="remove-column-btn"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="font-mono font-bold w-8 text-center">{config.columns}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/compartments/add-column`);
                    toast.success("Spalte hinzugefügt");
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Fehler");
                  }
                }}
                data-testid="add-column-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="border-slate-200">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-slate-500">Legende:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded slot-empty border" />
              <span>Leer</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded slot-low border" />
              <span>Niedrig (&lt;30%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded slot-ok border" />
              <span>OK (30-70%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded slot-full border" />
              <span>Voll (&gt;70%)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vending Grid */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Grid3X3 className="w-5 h-5" />
            Fächer-Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          {compartments.length > 0 ? (
            <div className="space-y-2">
              {rows.map((row, rowIndex) => (
                <div 
                  key={rowIndex} 
                  className="grid gap-2"
                  style={{ gridTemplateColumns: `repeat(${config.columns}, minmax(0, 1fr))` }}
                >
                  {row.map((comp) => (
                    <SlotCard
                      key={comp.id}
                      compartment={comp}
                      products={products}
                      onUpdate={fetchData}
                      allCompartments={compartments}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Grid3X3 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">Keine Fächer vorhanden</p>
              <Button 
                onClick={() => setConfigOpen(true)}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Fächer generieren
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Gesamtfächer</div>
            <div className="text-2xl font-heading font-bold">{compartments.length}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Belegt</div>
            <div className="text-2xl font-heading font-bold text-emerald-600">
              {compartments.filter(c => c.product_id).length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Nachfüllen nötig</div>
            <div className="text-2xl font-heading font-bold text-amber-600">
              {compartments.filter(c => c.product_id && (c.current_stock / c.max_capacity) < 0.3).length}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VendingPage;
